import {
    Input,
    Component,
    OnInit,
    ContentChild,
    TemplateRef,
    ElementRef
} from '@angular/core';
import { TranslationService } from '../../translations';

type KeyFrag = string | { id: number; text: string };

/*
This is intended tio handle keys that have parts that interact with code.
For example: "TO see some stuff got to Employee Directory and look around" with
"Employee Directory" being a link. In Drupal these keys are represented as
"blah <a data-id="0"> blah</a> blah. In code you can do something like
    <pi-complex-key [key]="'selfIdentity.noMatchHaveNotTaken'">
        <ng-template #fragmentHandler let-keyFrag="keyFrag">
            <ng-container [ngSwitch]="keyFrag.id">
                <span
                    *ngSwitchCase="0"
                    (click)="navigateToAssessmentCenter()"
                    class="lsg-link selfIdentityLink"
                >
                    {{keyFrag.text}}
                </span>
            </ng-container>
        </ng-template>
    </pi-complex-key>
This will use the key mentioned in the first line and replace sections marked
with a data-id with what you have inside the pi-complex-key element
*/

@Component({
    selector: 'pi-complex-key',
    template: `
        @for (keyFrag of keyData; track keyFrag) {
            @if (!isIdFrag(keyFrag)) {
                @if (!isHtmlKey) {
                    {{ keyFrag }}
                } @else {
                    <span [innerHtml]="keyFrag"></span>
                }
            }
            @if (isIdFrag(keyFrag)) {
                <ng-template
                    [ngTemplateOutlet]="contentTemplate"
                    [ngTemplateOutletContext]="{
                        keyFrag: keyFrag
                    }"
                >
                </ng-template>
            }
        }
    `,
    standalone: false
})
export class ComplexKeyComponent implements OnInit {
    @Input()
    key: string;

    @Input()
    isHtmlKey: boolean;

    @ContentChild('fragmentHandler', { static: false })
    contentTemplate: TemplateRef<ElementRef>;

    keyData: KeyFrag[];

    constructor(public TR: TranslationService) {}

    keySplitter = /<a\s+data-id="([\w]*)">([^<]*)<\/a>/;

    ngOnInit() {
        this.keyData = this.decomposeKey(this.key);
    }

    decomposeKey(keyName: string): KeyFrag[] {
        const keyFrags = [];
        const keyPieces = this.TR.key(keyName)
            .split(this.keySplitter)
            .filter((_) => _);
        for (let i = 0; i < keyPieces.length; i++) {
            if (!isNaN(keyPieces[i] as any) && i < keyPieces.length - 1) {
                keyFrags.push({
                    id: parseInt(keyPieces[i], 10),
                    text: keyPieces[i + 1]
                });
                i += 1;
            } else {
                keyFrags.push(keyPieces[i]);
            }
        }
        return keyFrags;
    }

    isIdFrag(keyFrag: KeyFrag): keyFrag is { id: number; text: string } {
        return typeof keyFrag !== 'string';
    }
}
